import { MaybePromise, RxCollection, SyncOptionsGraphQL, WithDeleted } from 'rxdb';
import {
  GraphQLServerUrl,
  GraphQLSyncPullOptions,
  GraphQLSyncPushOptions,
  RxGraphQLPullResponseModifier,
  RxGraphQLPushResponseModifier,
  RxGraphQLReplicationPullQueryBuilder,
  RxGraphQLReplicationPushQueryBuilder,
} from 'rxdb/dist/types/types/plugins/replication-graphql';
import { replicateGraphQL, RxGraphQLReplicationState } from 'rxdb/plugins/replication-graphql';

import { rootStore } from '@/mobx-models/Root';
import { SYNC_LIMIT_LOW } from '@/utilities/constants';

import { apiConfig } from '../../config/api.config';
import { LiveInterval } from '../constants';
import { CheckpointType } from '../types';
import { Crumbs } from './bakery/Crumbs';
import { Database } from './Database';
import { pullResponseModifier } from './pullResponseModifier';
import { RxdbCollectionNameV2 } from './RxdbCollectionNameV2';

export type BuildConfig<D> = (collectionName: RxdbCollectionNameV2) => Config<D>;
export type Config<D> = SyncOptionsGraphQL<D, CheckpointType>;
export type ConfigBuilder = <D>(collectionName: RxdbCollectionNameV2) => Config<D>;
export type PullConfig<D> = GraphQLSyncPullOptions<D, CheckpointType>;
export type PullModifier<D> = (docData: unknown) => MaybePromise<WithDeleted<D>>;
export type PullQueryBuilder = RxGraphQLReplicationPullQueryBuilder<CheckpointType>;
export type PullResponseModifier<D> = RxGraphQLPullResponseModifier<D, CheckpointType>;
export type PushConfig<D> = GraphQLSyncPushOptions<D>;
export type PushQueryBuilder = RxGraphQLReplicationPushQueryBuilder;
export type PushModifier<D> = (docData: WithDeleted<D>) => MaybePromise<unknown>;
export type PushResponseModifier<D> = RxGraphQLPushResponseModifier<D>;
export type ReplicationState<D> = RxGraphQLReplicationState<D, CheckpointType>;

export const baseConfig: Partial<Config<unknown>> = {
  deletedField: 'isDeleted',
  live: true,
  retryTime: 3000, // on failed mutation retry, this reduces the number of foreign key constraint errors,
};

export const basePullConfig: Partial<PullConfig<unknown>> = {
  batchSize: SYNC_LIMIT_LOW,
  responseModifier: pullResponseModifier,
};

// We can accept any replication state; there's nothing state-specific here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function crumbleReplicationState(replicationState: ReplicationState<any>): Crumbs {
  const { collection, isStopped, url, autoStart, live } = replicationState;
  return {
    collection: collection.name,
    isStopped,
    url: url.http,
    autoStart,
    live,
  };
}

export const noModifications: PushResponseModifier<unknown> = async () => [];

export const basePushConfig: Partial<PushConfig<unknown>> = {
  batchSize: 1,
  responseModifier: noModifications,
};

export function buildUrl(collectionName: string, baseUrl = apiConfig.graphUrl): GraphQLServerUrl {
  return { http: `${baseUrl}?collection=${collectionName}` };
}

// It doesn't matter what type of config we have.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function bypassDeleted(config: Config<any>) {
  // Mutation is intentional; this is here to abstract V1 functionality in syncGraphQLFor.
  // eslint-disable-next-line no-param-reassign
  config.deletedField = 'notUsed';
}

export function getCollection(rxdb: Database, collectionName: RxdbCollectionNameV2): RxCollection {
  const collection = rxdb.collections[collectionName];
  if (collection === null)
    throw new Error('Collection name not mapped to BorerDatabaseCollections');
  return collection;
}

export function createConfig<D>(
  idToken: string,
  rxdb: Database,
  collectionName: RxdbCollectionNameV2,
): Config<D> {
  const config: Config<D> = {
    ...(baseConfig as Partial<Config<D>>),
    collection: getCollection(rxdb, collectionName),
    headers: {
      Authorization: idToken,
      ...(rootStore.user.siteId && { siteId: rootStore.user.siteId }),
    },
    url: buildUrl(collectionName),
  };
  return config;
}

export function createConfigBuilder(idToken: string, rxdb: Database): ConfigBuilder {
  return collectionName => createConfig(idToken, rxdb, collectionName);
}

export function doNotAutoSync<D>(config: Config<D>) {
  // Mutation is intentional; this is abstraction of necessary functionality.
  // eslint-disable-next-line no-param-reassign
  config.live = false;
  // eslint-disable-next-line no-param-reassign
  config.autoStart = false;
}

export function pull<D>(
  config: Config<D>,
  queryBuilder: PullQueryBuilder,
  customResponseModifier?: PullResponseModifier<D>,
): PullConfig<D> {
  // Mutation is intentional; this is abstraction of necessary functionality.
  // eslint-disable-next-line no-param-reassign
  config.pull = {
    ...(basePullConfig as Partial<PullConfig<D>>),
    queryBuilder,
    ...(customResponseModifier && { responseModifier: customResponseModifier }),
  };
  return config.pull;
}

export function push<D>(config: Config<D>, queryBuilder: PushQueryBuilder): PushConfig<D> {
  // Mutation is intentional; this is abstraction of necessary functionality.
  // eslint-disable-next-line no-param-reassign
  config.push = {
    ...(basePushConfig as Partial<PushConfig<D>>),
    queryBuilder,
  };
  return config.push;
}

export function replicate<D>(config: Config<D>): ReplicationState<D> {
  // This doesn't do much. However, it is useful because:
  //   1. There are two different `replicateGraphQL` functions in RxDB that Intellisense will detect & suggest.
  //   2. Picking the wrong one breaks our build process with this error:
  //     `[vite-plugin-pwa:build] Missing "./dist/types/plugins/replication-graphql" specifier in "rxdb" package`
  // So, instead of trying to pick the right one every time, just call `replicate()` and rest assured that you're getting the right one.
  return replicateGraphQL(config);
}

export function schedule<D>(
  replicationState: ReplicationState<D>,
  syncInterval = LiveInterval.SECONDS_30,
): NodeJS.Timeout {
  const interval = setInterval(() => replicationState.reSync(), syncInterval);
  return interval;
}
